import { apiGetDocs } from '../api/docs';
import { deleteItem, buildFlatItems } from './projectHelpers';
import { renameDoc } from './docHelpers';
import { createItemComparator } from './sortHelpers';
import { normalizeEditorContent } from './editorHelpers';
import { createChatMessageNode } from './ai';
import { findItem } from './mapKey';

const AI_STAGE_ORDER = ['intent', 'plan', 'retrieve', 'summarize', 'select', 'request', 'stream', 'finalize'];
const CHAT_STATUS_VARIANTS = new Set(['stage', 'chip']);
const normalizeChatStatusVariant = (value = null) => {
  const normalized = String(value || '').trim().toLowerCase();
  return CHAT_STATUS_VARIANTS.has(normalized) ? normalized : null;
};
const AI_STAGE_MESSAGES = {
  intent: 'Gathering intent...',
  plan: 'Building retrieval plan...',
  retrieve: 'Retrieving relevant context...',
  summarize: 'Summarizing...',
  select: 'Selecting the best context...',
  request: 'Preparing response request...',
  stream: 'Generating response...',
  finalize: 'Finalizing response...'
};

const sortChatsByRecent = createItemComparator('recent', {
  dateKeys: ['updated_at', 'created_at']
});

export const listChatsForProject = async (projectId) => {
  if (!projectId) return [];

  const docs = await apiGetDocs(projectId);
  return buildFlatItems({
    groups: [
      {
        items: docs,
        mapItem: (doc) => ({ ...doc, type: 'doc' })
      }
    ],
    filterItem: (doc) => doc?.docType === 'chat',
    sortItems: sortChatsByRecent
  });
};

export const renameChat = async ({
  chat,
  nextName,
  itemsMap,
  setItemsMap,
  tabs,
  setTabs,
  projectId,
  userId
}) => {
  if (!chat?.id) return;
  const trimmed = String(nextName || '').trim();
  if (!trimmed) return;

  const renamed = await renameDoc(chat.id, trimmed, itemsMap, setItemsMap, projectId, userId);
  if (!renamed) return null;

  if (setTabs) {
    setTabs((prev) => (Array.isArray(prev)
      ? prev.map((tab) => (String(tab?.docId) === String(chat.id)
        ? { ...tab, label: trimmed }
        : tab))
      : prev));
  }

  return renamed;
};

export const deleteChat = async ({
  chat,
  projectId,
  userId,
  itemsMap,
  setItemsMap,
  tabs,
  setTabs,
  setActiveTabId
}) => {
  if (!chat?.id || !projectId) return;

  await deleteItem(
    { ...chat, type: 'doc' },
    projectId,
    userId,
    itemsMap,
    setItemsMap,
    tabs,
    setTabs,
    setActiveTabId
  );
};

export const printChat = (chat, onOpenChat) => {
  onOpenChat?.(chat, 'replace');
  window.setTimeout(() => {
    window.print();
  }, 80);
};

export const buildChatComposerNextJson = ({
  content,
  userText,
  assistantText,
  assistantTimestamp,
  assistantModel = null,
  assistantStatusVariant = null,
  assistantProgressText = null,
  assistantArtifactText = null,
  now = Date.now(),
  orchestration = null
} = {}) => {
  // Keep orchestration payload accepted here so we can wire model/mode choices
  // into future assistant backends without changing composer/component contracts.
  void orchestration;

  const normalized = normalizeEditorContent(content) || { json: { type: 'doc', content: [] }, html: '' };
  const currentJson = normalized?.json && typeof normalized.json === 'object'
    ? normalized.json
    : { type: 'doc', content: [] };

  const baseDoc = currentJson && typeof currentJson === 'object'
    ? currentJson
    : { type: 'doc', content: [] };
  const contentNodes = Array.isArray(baseDoc.content) ? baseDoc.content : [];

  const trimmedUser = String(userText || '').trim();
  if (!trimmedUser) return { ...baseDoc, type: 'doc', content: contentNodes };

  const hasAssistantTextInput = assistantText !== undefined;
  const rawAssistantText = hasAssistantTextInput ? String(assistantText || '') : '';
  const trimmedAssistant = hasAssistantTextInput
    ? rawAssistantText
    : 'I could not generate a response yet.';

  const userNode = createChatMessageNode({
    role: 'user',
    text: trimmedUser,
    timestamp: now
  });

  const assistantNode = createChatMessageNode({
    role: 'ai',
    text: trimmedAssistant,
    timestamp: Number(assistantTimestamp || (Number(now) + 1)),
    parseMarkdown: true,
    modelLabel: String(assistantModel || '').trim() || null,
    statusVariant: normalizeChatStatusVariant(assistantStatusVariant),
    progressText: String(assistantProgressText || '').trim() || null,
    artifactText: String(assistantArtifactText || '').trim() || null
  });

  return {
    ...baseDoc,
    type: 'doc',
    content: [...contentNodes, userNode, assistantNode]
  };
};

export const buildAiStageStatusText = ({
  activeStage,
  completedStages = [],
  stageSummary = ''
} = {}) => {
  void completedStages;
  void stageSummary;
  const normalizedActive = String(activeStage || '').trim().toLowerCase();
  const baseLine = AI_STAGE_MESSAGES[normalizedActive] || 'Working on your response...';
  return baseLine;
};

export const buildAiStageCompletionSummary = ({
  activeStage,
  completedStages = [],
  stageSummary = ''
} = {}) => {
  const normalizedActive = String(activeStage || '').trim().toLowerCase();
  const completedSet = new Set(
    (Array.isArray(completedStages) ? completedStages : [])
      .map(value => String(value || '').trim().toLowerCase())
      .filter(value => AI_STAGE_ORDER.includes(value))
  );

  if (normalizedActive && AI_STAGE_ORDER.includes(normalizedActive)) {
    completedSet.add(normalizedActive);
  }

  const completedCount = AI_STAGE_ORDER.filter((stageKey) => completedSet.has(stageKey)).length;
  const maxStages = AI_STAGE_ORDER.length;
  const summary = String(stageSummary || '').trim();

  return summary
    ? `Activity complete (${completedCount}/${maxStages}): ${summary}`
    : `Activity complete (${completedCount}/${maxStages})`;
};

export const replaceChatMessageTextByTimestamp = ({
  content,
  messageTimestamp,
  nextText,
  textMarks,
  statusVariant,
  progressText,
  modelLabel,
  requestKey,
  artifactText
} = {}) => {
  const normalized = normalizeEditorContent(content) || { json: { type: 'doc', content: [] }, html: '' };
  const currentJson = normalized?.json && typeof normalized.json === 'object'
    ? normalized.json
    : { type: 'doc', content: [] };

  const baseDoc = currentJson && typeof currentJson === 'object'
    ? currentJson
    : { type: 'doc', content: [] };
  const contentNodes = Array.isArray(baseDoc.content) ? baseDoc.content : [];

  const targetTimestamp = Number(messageTimestamp || 0);
  if (!targetTimestamp || !contentNodes.length) return baseDoc;

  let replaced = false;
  const nextNodes = contentNodes.map((node) => {
    if (replaced) return node;
    const isChatMessage = node?.type === 'chatMessage';
    const role = String(node?.attrs?.role || '').toLowerCase();
    const timestamp = Number(node?.attrs?.timestamp || 0);
    const isAssistant = role === 'ai' || role === 'assistant';
    if (isChatMessage && isAssistant && timestamp === targetTimestamp) {
      replaced = true;
      const hasNextText = nextText !== undefined;
      const normalizedProgressText = progressText === undefined
        ? node?.attrs?.progressText ?? null
        : (String(progressText || '').trim() || null);
      const normalizedStatusVariant = statusVariant === undefined
        ? normalizeChatStatusVariant(node?.attrs?.statusVariant)
        : normalizeChatStatusVariant(statusVariant);
      const normalizedModelLabel = modelLabel === undefined
        ? (String(node?.attrs?.modelLabel || '').trim() || null)
        : (String(modelLabel || '').trim() || null);
      const normalizedRequestKey = requestKey === undefined
        ? (String(node?.attrs?.requestKey || '').trim() || null)
        : (String(requestKey || '').trim() || null);
      const normalizedArtifactText = artifactText === undefined
        ? (String(node?.attrs?.artifactText || '').trim() || null)
        : (String(artifactText || '').trim() || null);

      if (!hasNextText) {
        return {
          ...node,
          attrs: {
            ...(node?.attrs || {}),
            role: 'ai',
            timestamp: targetTimestamp,
            statusVariant: normalizedStatusVariant,
            progressText: normalizedProgressText,
            modelLabel: normalizedModelLabel,
            requestKey: normalizedRequestKey,
            artifactText: normalizedArtifactText
          }
        };
      }

      return createChatMessageNode({
        role: 'ai',
        text: String(nextText || '').trim() || 'I could not generate a response yet.',
        timestamp: targetTimestamp,
        parseMarkdown: true,
        textMarks: Array.isArray(textMarks) ? textMarks : null,
        statusVariant: normalizedStatusVariant,
        progressText: normalizedProgressText,
        modelLabel: normalizedModelLabel,
        requestKey: normalizedRequestKey,
        artifactText: normalizedArtifactText
      });
    }
    return node;
  });

  if (!replaced && nextText !== undefined) {
    const replacementNode = createChatMessageNode({
      role: 'ai',
      text: String(nextText || '').trim() || 'I could not generate a response yet.',
      timestamp: targetTimestamp,
      parseMarkdown: true,
      statusVariant: normalizeChatStatusVariant(statusVariant),
      progressText: String(progressText || '').trim() || null,
      modelLabel: String(modelLabel || '').trim() || null,
      artifactText: String(artifactText || '').trim() || null
    });

    return {
      ...baseDoc,
      type: 'doc',
      content: [...contentNodes, replacementNode]
    };
  }

  return {
    ...baseDoc,
    type: 'doc',
    content: nextNodes
  };
};

export const appendChatMessageText = ({
  content,
  role = 'system',
  text,
  timestamp = Date.now(),
  parseMarkdown = false,
  statusVariant = null
} = {}) => {
  const normalized = normalizeEditorContent(content) || { json: { type: 'doc', content: [] }, html: '' };
  const currentJson = normalized?.json && typeof normalized.json === 'object'
    ? normalized.json
    : { type: 'doc', content: [] };

  const baseDoc = currentJson && typeof currentJson === 'object'
    ? currentJson
    : { type: 'doc', content: [] };
  const contentNodes = Array.isArray(baseDoc.content) ? baseDoc.content : [];
  const trimmedText = String(text || '').trim();
  if (!trimmedText) return baseDoc;

  const node = createChatMessageNode({
    role,
    text: trimmedText,
    timestamp,
    parseMarkdown,
    statusVariant
  });

  return {
    ...baseDoc,
    type: 'doc',
    content: [...contentNodes, node]
  };
};

export const insertChatMessageTextBeforeTimestamp = ({
  content,
  beforeTimestamp,
  role = 'system',
  text,
  timestamp = Date.now(),
  parseMarkdown = false,
  statusVariant = null
} = {}) => {
  const normalized = normalizeEditorContent(content) || { json: { type: 'doc', content: [] }, html: '' };
  const currentJson = normalized?.json && typeof normalized.json === 'object'
    ? normalized.json
    : { type: 'doc', content: [] };

  const baseDoc = currentJson && typeof currentJson === 'object'
    ? currentJson
    : { type: 'doc', content: [] };
  const contentNodes = Array.isArray(baseDoc.content) ? baseDoc.content : [];
  const trimmedText = String(text || '').trim();
  const targetTimestamp = Number(beforeTimestamp || 0);
  if (!trimmedText || !targetTimestamp) return baseDoc;

  const node = createChatMessageNode({
    role,
    text: trimmedText,
    timestamp,
    parseMarkdown,
    statusVariant
  });

  let inserted = false;
  const nextNodes = [];
  for (const existingNode of contentNodes) {
    if (!inserted) {
      const isChatMessage = existingNode?.type === 'chatMessage';
      const roleValue = String(existingNode?.attrs?.role || '').toLowerCase();
      const existingTimestamp = Number(existingNode?.attrs?.timestamp || 0);
      const isAssistant = roleValue === 'ai' || roleValue === 'assistant';
      if (isChatMessage && isAssistant && existingTimestamp === targetTimestamp) {
        nextNodes.push(node);
        inserted = true;
      }
    }
    nextNodes.push(existingNode);
  }

  return {
    ...baseDoc,
    type: 'doc',
    content: inserted ? nextNodes : [...contentNodes, node]
  };
};

export const countChatMessagesInDocJson = (docJson) => {
  const content = Array.isArray(docJson?.content) ? docJson.content : [];
  return content.filter((node) => node?.type === 'chatMessage').length;
};

export const shouldPinChatToBottom = ({ isChatDoc = false, docJson, minMessages = 2 } = {}) => {
  if (!isChatDoc) return false;
  const messageCount = countChatMessagesInDocJson(docJson);
  return messageCount >= Math.max(1, Number(minMessages || 2));
};

export const buildLinkedDocChipOptions = ({
  isActiveChatTab = false,
  activeDocId = null,
  selectedDocId = null,
  sourceTabs = [],
  oppositePaneActiveTabId = null,
  itemsMap,
  maxLinkedDocs = 6
} = {}) => {
  if (!isActiveChatTab) return [];
  const activeDocNumericId = Number(activeDocId);
  const activeDocKey = String(activeDocNumericId || '');
  const tabs = Array.isArray(sourceTabs) ? sourceTabs : [];
  const itemDocType = (docId) => String(findItem(itemsMap, docId, 'doc')?.docType || '').trim().toLowerCase();
  const getLabelForDocId = (docId, fallbackLabel = '') => {
    const item = findItem(itemsMap, docId, 'doc');
    return String(item?.name || fallbackLabel || '').trim() || `Doc ${docId}`;
  };
  const isEligibleLinkedDocId = (docId) => {
    const numericId = Number(docId);
    if (!Number.isFinite(numericId) || numericId <= 0) return false;
    if (String(numericId) === activeDocKey) return false;
    if (itemDocType(numericId) === 'chat') return false;
    return true;
  };

  const oppositeActiveTab = tabs.find(tab => String(tab?.id || '') === String(oppositePaneActiveTabId || ''));
  const oppositeDocId = Number(oppositeActiveTab?.docId);
  const selectedDocNumericId = Number(selectedDocId);

  const fallbackTab = tabs.find((tab) => {
    const numericId = Number(tab?.docId);
    const docType = String(tab?.docType || '').trim().toLowerCase();
    if (!Number.isFinite(numericId) || numericId <= 0) return false;
    if (String(numericId) === activeDocKey) return false;
    if (docType === 'chat') return false;
    return itemDocType(numericId) !== 'chat';
  });

  const candidateEntries = [
    {
      id: oppositeDocId,
      label: getLabelForDocId(oppositeDocId, oppositeActiveTab?.label || '')
    },
    {
      id: selectedDocNumericId,
      label: getLabelForDocId(selectedDocNumericId)
    },
    {
      id: Number(fallbackTab?.docId),
      label: getLabelForDocId(Number(fallbackTab?.docId), fallbackTab?.label || '')
    }
  ];

  const seen = new Set();
  const options = [];
  const maxOptions = Math.max(1, Number(maxLinkedDocs || 6));
  for (const entry of candidateEntries) {
    const numericId = Number(entry?.id);
    if (!isEligibleLinkedDocId(numericId)) continue;
    const key = String(numericId);
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({ id: numericId, label: entry.label });
    if (options.length >= maxOptions || options.length >= 1) break;
  }

  return options;
};

export const resolveLinkedDocOptionCandidate = ({
  candidateId,
  activeDocId = null,
  sourceTabs = [],
  itemsMap
} = {}) => {
  const numericId = Number(candidateId);
  if (!Number.isFinite(numericId) || numericId <= 0) return null;
  if (String(numericId) === String(activeDocId || '')) return null;

  const item = findItem(itemsMap, numericId, 'doc');
  const tabs = Array.isArray(sourceTabs) ? sourceTabs : [];
  const tab = tabs.find(entry => String(entry?.docId || '') === String(numericId));
  return {
    id: numericId,
    label: String(item?.name || tab?.label || '').trim() || `Doc ${numericId}`
  };
};
